// Pure mappers between the recurrence dialog state (the `recurrence*` camelCase
// keys used by RecurrenceOptions / INITIAL_TASK_STATE) and the flat
// `recurrence_*` fields stored on a task row — personal (Supabase) or shared
// (Mongo shared item). No React, no network: safe to unit test and to import
// from anywhere.
//
// Monthly special modes are encoded in recurrence_day_of_month:
//   -1 = last day of month, -2 = last <weekday> of month,
//   -11..-14 = 1st..4th <weekday> of month (weekday in recurrence_days_of_week[0])

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const SHORT_DAYS = { sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Is this day-of-month sentinel the "nth weekday" family (-2 last, -11..-14 1st..4th)? */
const isWeekdaySentinel = (dom) => dom === -2 || (dom <= -11 && dom >= -14);

/** Whole number inside [lo, hi]; anything unparseable (e.g. a cleared input) → 1. */
const clampInt = (v, lo, hi) => {
  const n = parseInt(v, 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(hi, Math.max(lo, n));
};

/**
 * Map dialog recurrence state → backend payload fields.
 * Every consumer that POSTs/PATCHes a recurrence rule uses exactly this shape.
 * The numeric fields are clamped here — the inputs allow a momentarily empty
 * value, and '' must never reach the server as an interval or a count.
 */
export function recurrencePayloadFields(d) {
  const monthly = d.recurrenceType === 'monthly';
  const mode = d.recurrenceMonthlyMode || 'day';
  const weekdayMode = mode === 'last_weekday' || mode === 'nth_weekday';
  return {
    recurrence_type: d.recurrenceType,
    recurrence_interval: clampInt(d.recurrenceInterval, 1, 30),
    recurrence_days_of_week: d.recurrenceType === 'weekly'
      ? d.recurrenceDaysOfWeek
      : (monthly && weekdayMode ? [d.recurrenceWeekday || 'friday'] : null),
    recurrence_day_of_month: monthly
      ? (mode === 'last_day' ? -1
        : mode === 'last_weekday' ? -2
        : mode === 'nth_weekday' ? (d.recurrenceNth === 'last' ? -2 : -(10 + (d.recurrenceNth || 1)))
        : d.recurrenceDayOfMonth)
      : null,
    recurrence_end_type: d.recurrenceEndType,
    recurrence_end_date: d.recurrenceEndType === 'date' ? d.recurrenceEndDate : null,
    recurrence_count: d.recurrenceEndType === 'count' ? clampInt(d.recurrenceCount, 1, 365) : null,
  };
}

/**
 * Reverse of recurrencePayloadFields: a task row (personal parent row or shared
 * item) → the ten `recurrence*` dialog keys, with the same defaults the edit
 * dialogs have always used. A row with no recurrence fields yields the
 * INITIAL_TASK_STATE defaults, so opening an old task is always safe.
 */
export function recurrenceStateFromRow(row) {
  const r = row || {};
  const dom = r.recurrence_day_of_month;
  const weekdayMode = isWeekdaySentinel(dom);
  return {
    recurrenceType: r.recurrence_type || 'weekly',
    recurrenceInterval: r.recurrence_interval || 1,
    recurrenceDaysOfWeek: r.recurrence_days_of_week || [],
    recurrenceDayOfMonth: dom && dom > 0 ? dom : 1,
    recurrenceMonthlyMode: dom === -1 ? 'last_day' : weekdayMode ? 'nth_weekday' : 'day',
    recurrenceNth: dom === -2 ? 'last' : (dom <= -11 && dom >= -14) ? (-dom - 10) : 1,
    recurrenceWeekday: weekdayMode ? (r.recurrence_days_of_week?.[0] || 'friday') : 'friday',
    recurrenceEndType: r.recurrence_end_date ? 'date' : r.recurrence_count ? 'count' : 'never',
    recurrenceEndDate: r.recurrence_end_date || '',
    recurrenceCount: r.recurrence_count || 10,
  };
}

const ordinal = (n) => {
  const abs = Math.abs(Number(n) || 0);
  const rem100 = abs % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${abs}th`;
  const rem10 = abs % 10;
  return `${abs}${rem10 === 1 ? 'st' : rem10 === 2 ? 'nd' : rem10 === 3 ? 'rd' : 'th'}`;
};

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// 'yyyy-MM-dd' → 'Mar 3'. Parsed by hand so no timezone can shift the day.
const shortDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return String(iso || '');
  const month = MONTHS[parseInt(m[2], 10) - 1];
  if (!month) return String(iso || '');
  return `${month} ${parseInt(m[3], 10)}`;
};

/**
 * Short human sentence for a recurrence rule on a task row, e.g.
 * "Repeats every 2 weeks on Mon, Fri until Mar 3".
 * Returns '' for a row with no rule.
 */
export function describeRecurrence(row) {
  const r = row || {};
  const type = r.recurrence_type;
  if (!type) return '';
  const every = Math.max(1, parseInt(r.recurrence_interval, 10) || 1);
  const unit = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[type];
  if (!unit) return '';
  const cadence = every > 1
    ? `Repeats every ${every} ${unit}s`
    : `Repeats ${{ daily: 'daily', weekly: 'weekly', monthly: 'monthly', yearly: 'yearly' }[type]}`;

  let text = cadence;
  if (type === 'weekly') {
    const days = (r.recurrence_days_of_week || [])
      .filter((d) => WEEKDAYS.includes(d))
      .sort((a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b))
      .map((d) => SHORT_DAYS[d]);
    if (days.length) text += ` on ${days.join(', ')}`;
  } else if (type === 'monthly') {
    const dom = r.recurrence_day_of_month;
    const weekday = titleCase(r.recurrence_days_of_week?.[0] || 'friday');
    if (dom === -1) text += ' on the last day';
    else if (dom === -2) text += ` on the last ${weekday}`;
    else if (dom <= -11 && dom >= -14) text += ` on the ${ordinal(-dom - 10)} ${weekday}`;
    else if (dom > 0) text += ` on the ${ordinal(dom)}`;
  }

  if (r.recurrence_end_date) text += ` until ${shortDate(r.recurrence_end_date)}`;
  else if (r.recurrence_count) text += ` · ${r.recurrence_count} time${Number(r.recurrence_count) === 1 ? '' : 's'}`;
  return text;
}
